import { Socket, type Channel } from 'phoenix';

// Thin Absinthe-over-Phoenix subscription client (C29).
//
// Absinthe exposes GraphQL subscriptions over a Phoenix Socket using a well-known protocol:
//
//   1. Open ONE socket:  new Socket("/socket", { params: { token } })  then socket.connect().
//      The backend UserSocket.connect reads the JWT from the "token" param and REJECTS the
//      connection without a valid operator token — so we never connect without a token.
//   2. Join the control channel:  socket.channel("__absinthe__:control").join().
//   3. Start a subscription:  channel.push("doc", { query, variables }) — the reply payload
//      carries a `subscriptionId`. Absinthe then pushes `"subscription:data"` events on the
//      SAME control channel; each event payload is { subscriptionId, result: { data } }. We match
//      on subscriptionId and fan the result out to the right subscriber.
//   4. Stop a subscription:  channel.push("unsubscribe", { subscriptionId }).
//
// ── Single shared, ref-counted socket ────────────────────────────────────────────────────────
// Every subscription in the app shares ONE Socket + ONE control channel (ref-counted): the first
// subscriber connects + joins; subsequent subscribers reuse the live connection. When the LAST
// subscriber unsubscribes we leave the channel and disconnect the socket — no leaked sockets, no
// duplicate connections. The token is captured at connect time; if it changes the manager must be
// recreated (callers pass the current token — see useSubscription).
//
// ── Auth / degraded state ────────────────────────────────────────────────────────────────────
// The socket may close (e.g. the JWT expired mid-stream and the backend dropped the connection).
// We surface this via onStatusChange("closed"|"errored") so the UI can degrade to a
// "live paused — reconnect" affordance rather than crash. The raw token is NEVER logged.

/** The Absinthe control channel topic. Absinthe always uses this fixed topic. */
const ABSINTHE_CONTROL_TOPIC = '__absinthe__:control';

/** The Phoenix socket mount point (matches the backend `socket "/socket", UserSocket`). */
export const SOCKET_ENDPOINT = '/socket';

/** Connection status surfaced to subscribers so the UI can show a degraded/reconnect state. */
export type SocketStatus = 'connecting' | 'open' | 'closed' | 'errored';

/** A single Absinthe `subscription:data` push: `{ result: { data } }` matched by subscriptionId. */
export interface SubscriptionDataEvent<TData> {
  subscriptionId: string;
  result: { data?: TData | null; errors?: ReadonlyArray<{ message: string }> };
}

/** Per-subscription callbacks. `onData` receives the typed `result.data` of each push. */
export interface SubscriptionHandlers<TData> {
  onData: (data: TData) => void;
  onError?: ((message: string) => void) | undefined;
}

/** Handle returned by `subscribe`; call `unsubscribe()` to tear this subscription down. */
export interface SubscriptionHandle {
  unsubscribe: () => void;
}

/**
 * Phoenix Socket factory — injectable so tests can supply a fake Socket without a real WebSocket.
 * Defaults to the real `phoenix` Socket.
 */
export type SocketFactory = (endpoint: string, token: string) => Socket;

const defaultSocketFactory: SocketFactory = (endpoint, token) =>
  new Socket(endpoint, { params: { token } });

interface ActiveSubscription {
  /** Absinthe subscriptionId, populated once the `doc` push is acked. */
  subscriptionId: string | null;
  /**
   * Set when `unsubscribe()` runs before the `doc` ack arrives. The id isn't known yet, so we can't
   * push `"unsubscribe"` at teardown time; the ack handler flushes it once the id lands so the
   * server stops emitting (otherwise the subscription is orphaned and keeps streaming).
   */
  pendingUnsubscribe: boolean;
  query: string;
  variables: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handlers: SubscriptionHandlers<any>;
}

/**
 * Manages ONE shared, ref-counted Phoenix socket + Absinthe control channel for the lifetime of
 * its subscribers. Construct with the current operator token; `subscribe` lazily connects on the
 * first subscriber and disconnects after the last one leaves.
 */
export class AbsintheSocketManager {
  private socket: Socket | null = null;
  private channel: Channel | null = null;
  private readonly subscriptions = new Set<ActiveSubscription>();
  private status: SocketStatus = 'closed';
  private readonly statusListeners = new Set<(status: SocketStatus) => void>();

  constructor(
    private readonly token: string,
    private readonly factory: SocketFactory = defaultSocketFactory,
    private readonly endpoint: string = SOCKET_ENDPOINT,
  ) {}

  /** Current connection status. */
  getStatus(): SocketStatus {
    return this.status;
  }

  /** Subscribe to status changes (for the degraded/reconnect affordance). Returns an unsubscribe. */
  onStatusChange(listener: (status: SocketStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(next: SocketStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.statusListeners.forEach((listener) => listener(next));
  }

  /** Lazily open the socket + join the control channel on the first subscription. */
  private ensureConnected(): Channel {
    if (this.channel) return this.channel;

    const socket = this.factory(this.endpoint, this.token);
    this.socket = socket;
    this.setStatus('connecting');

    socket.onOpen(() => this.setStatus('open'));
    socket.onError(() => this.setStatus('errored'));
    socket.onClose(() => this.setStatus('closed'));

    socket.connect();

    // Intercept EVERY incoming frame so we can route Absinthe's `subscription:data` events
    // by their PER-DOC topic (the subscriptionId) without joining a channel on that topic.
    //
    // 2026-06-07: an earlier attempt joined a phoenix-js Channel on each subscriptionId so
    // the frame's `isMember` check passed — but server-side `Absinthe.Phoenix.Channel.join/3`
    // only pattern-matches on `"__absinthe__:control"`, so every per-doc join crashed the
    // channel process with FunctionClauseError. Backend logs filled with crash reports
    // and the connection had to be re-established constantly, causing the visible slowness.
    //
    // The correct shape: Absinthe broadcasts on the per-doc PubSub topic with fastlane
    // metadata that sends serialized frames DIRECTLY to the transport pid. We pick those
    // frames up here via `socket.onMessage`, which fires for every frame regardless of
    // whether any channel claims it. No client-side join, no server-side crash.
    socket.onMessage((msg: { topic?: string; event?: string; payload?: unknown }) => {
      if (msg?.event !== 'subscription:data') return;
      const topic = msg.topic;
      if (typeof topic !== 'string') return;
      // The wire payload Phoenix delivers is `{ result: { data, errors } }` — match what
      // the server's Absinthe broadcast wraps each push in. Pulling `.result` here keeps
      // the per-subscription dispatch logic clean.
      const wirePayload = msg.payload as
        | { result?: SubscriptionDataEvent<unknown>['result'] }
        | undefined;
      const result = wirePayload?.result;
      for (const sub of this.subscriptions) {
        if (sub.subscriptionId !== topic) continue;
        const errorMessage = result?.errors?.[0]?.message;
        if (errorMessage) {
          sub.handlers.onError?.(errorMessage);
          return;
        }
        const data = result?.data;
        if (data != null) sub.handlers.onData(data);
        return;
      }
    });

    const channel = socket.channel(ABSINTHE_CONTROL_TOPIC, {});
    this.channel = channel;

    channel
      .join()
      .receive('ok', () => this.setStatus('open'))
      .receive('error', () => this.setStatus('errored'))
      .receive('timeout', () => this.setStatus('errored'));

    return channel;
  }

  /**
   * Start a subscription. Connects on the first call; the returned handle's `unsubscribe()` tears
   * just this subscription down (and disconnects the shared socket once the last one leaves).
   */
  subscribe<TData>(
    query: string,
    variables: Record<string, unknown>,
    handlers: SubscriptionHandlers<TData>,
  ): SubscriptionHandle {
    const channel = this.ensureConnected();
    const entry: ActiveSubscription = {
      subscriptionId: null,
      pendingUnsubscribe: false,
      query,
      variables,
      handlers,
    };
    this.subscriptions.add(entry);

    channel
      .push('doc', { query, variables })
      .receive('ok', (reply: { subscriptionId?: string } | undefined) => {
        if (!reply?.subscriptionId) return;
        if (this.subscriptions.has(entry)) {
          // Just remember the id — the socket-level onMessage interceptor (installed in
          // ensureConnected) routes incoming `subscription:data` frames to the matching
          // entry by id. No client-side channel join is needed; joining the per-doc
          // topic would crash the server because `Absinthe.Phoenix.Channel.join/3` only
          // matches the literal `"__absinthe__:control"` topic.
          entry.subscriptionId = reply.subscriptionId;
        } else if (entry.pendingUnsubscribe) {
          // We were torn down before this ack arrived: the server registered the subscription and
          // is now emitting for an id we never had at teardown. Send a best-effort `"unsubscribe"`
          // for it now so the subscription isn't orphaned.
          this.channel?.push('unsubscribe', { subscriptionId: reply.subscriptionId });
        }
      })
      .receive('error', () => {
        handlers.onError?.('Subscription rejected');
      });

    return {
      unsubscribe: () => this.removeSubscription(entry),
    };
  }

  private removeSubscription(entry: ActiveSubscription): void {
    if (!this.subscriptions.has(entry)) return;
    this.subscriptions.delete(entry);

    if (entry.subscriptionId && this.channel) {
      // Tell Absinthe to stop emitting for this subscription (best-effort; channel may be gone).
      this.channel.push('unsubscribe', { subscriptionId: entry.subscriptionId });
    } else if (!entry.subscriptionId) {
      // Torn down before the `doc` ack landed: we don't have the id yet, so flag it and let the ack
      // handler push `"unsubscribe"` once the id arrives (otherwise the server keeps streaming).
      entry.pendingUnsubscribe = true;
    }

    // Last one out turns off the lights: leave the channel + disconnect the shared socket.
    if (this.subscriptions.size === 0) {
      this.teardown();
    }
  }

  /**
   * Forcibly tear down + reopen the WebSocket and re-subscribe every active subscription.
   * Used by the live-indicator click affordance: the operator clicks to validate the feed,
   * and we drop the (possibly silently-dead) socket and reopen it. Subscriptions keep
   * their handler references so the new subscriptionId routes back to the right caller.
   */
  reconnect(): void {
    // Snapshot the live subscriptions BEFORE teardown — teardown's defensive cleanup
    // empties the set.
    const snapshot: ActiveSubscription[] = [];
    for (const entry of this.subscriptions) {
      snapshot.push(entry);
      entry.subscriptionId = null;
      entry.pendingUnsubscribe = false;
    }
    this.teardown();
    this.subscriptions.clear();

    // Re-add each snapshot entry to the live set + restart the subscription. The new
    // socket's `onMessage` interceptor (installed in ensureConnected) routes incoming
    // `subscription:data` frames by their refreshed subscriptionId.
    for (const entry of snapshot) {
      this.subscriptions.add(entry);
      const channel = this.ensureConnected();
      channel
        .push('doc', { query: entry.query, variables: entry.variables })
        .receive('ok', (reply: { subscriptionId?: string } | undefined) => {
          if (!reply?.subscriptionId) return;
          if (!this.subscriptions.has(entry)) return;
          entry.subscriptionId = reply.subscriptionId;
        })
        .receive('error', () => {
          entry.handlers.onError?.('Subscription rejected');
        });
    }
  }

  /** Leave the channel + disconnect the socket. Idempotent. */
  private teardown(): void {
    this.subscriptions.clear();

    if (this.channel) {
      this.channel.leave();
      this.channel = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.setStatus('closed');
  }
}
